import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Eye, EyeOff } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { http, formatError } from "@/lib/api";
import { SCHEMAS } from "./schemas";
import { FieldInput } from "./FieldInput";

const Editor = ({ col, item, onClose, onSaved }) => {
  const schema = SCHEMAS[col];
  const [draft, setDraft] = useState(item || {});
  const [saving, setSaving] = useState(false);
  const invalid = Object.values(draft).some((v) => v === undefined);

  const save = async () => {
    setSaving(true);
    try {
      if (item?.id) await http.put(`/admin/content/${col}/${item.id}`, draft);
      else await http.post(`/admin/content/${col}`, draft);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
    setSaving(false);
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="admin-editor">
        <SheetHeader><SheetTitle>{item?.id ? `Edit: ${item[schema.titleKey] || ""}` : `New ${schema.label.toLowerCase()}`}</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-5 pb-24">
          {schema.fields.map((f) => (
            <FieldInput key={f.key} f={f} value={draft[f.key]} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} />
          ))}
        </div>
        <div className="fixed bottom-0 right-0 flex w-full gap-2 border-t bg-white p-4 sm:max-w-2xl">
          <button onClick={save} disabled={saving || invalid} className="btn-primary flex-1 !py-3 disabled:opacity-60" data-testid="admin-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
          <button onClick={onClose} className="btn-ghost !py-3" data-testid="admin-cancel-btn">Cancel</button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const ContentManager = ({ col }) => {
  const schema = SCHEMAS[col];
  const qc = useQueryClient();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await http.get(`/admin/content/${col}`); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); setEditing(null); }, [col]); // eslint-disable-line react-hooks/exhaustive-deps

  const saved = () => { setEditing(null); load(); qc.invalidateQueries({ queryKey: ["content"] }); };
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it[schema.titleKey]}"? This cannot be undone.`)) return;
    try { await http.delete(`/admin/content/${col}/${it.id}`); toast.success("Deleted"); saved(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid={`admin-content-${col}`}>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-extrabold text-[#13052A]">{schema.label}</h2>
        {schema.canCreate && <button onClick={() => setEditing({})} className="btn-primary !py-2.5" data-testid="admin-new-btn"><Plus className="h-4 w-4" /> Add new</button>}
      </div>
      {loading ? <Loader2 className="mt-8 h-6 w-6 animate-spin text-[#7C3AED]" /> : (
        <ul className="mt-6 divide-y divide-violet-100 rounded-2xl border border-violet-200 bg-white">
          {items.map((it) => (
            <li key={it.id} className="flex items-center justify-between gap-4 px-5 py-4" data-testid={`admin-item-${it.slug || it.id}`}>
              <div className="min-w-0">
                <p className="truncate font-bold text-[#13052A]">{it[schema.titleKey]}</p>
                <p className="truncate text-xs text-slate-500">{it.slug ? `/${it.slug}` : it.quote?.slice(0, 80) || it.price}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {col === "posts" && (it.published ? <span className="flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-bold text-green-700"><Eye className="h-3 w-3" /> Live</span>
                  : <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700"><EyeOff className="h-3 w-3" /> Draft</span>)}
                <button onClick={() => setEditing(it)} className="rounded-full p-2 text-[#581C87] hover:bg-violet-50" aria-label="Edit" data-testid={`admin-edit-${it.slug || it.id}`}><Pencil className="h-4 w-4" /></button>
                {schema.canCreate && <button onClick={() => remove(it)} className="rounded-full p-2 text-red-600 hover:bg-red-50" aria-label="Delete" data-testid={`admin-delete-${it.slug || it.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </li>
          ))}
          {!items.length && <li className="px-5 py-8 text-center text-sm text-slate-500">Nothing here yet.</li>}
        </ul>
      )}
      {editing && <Editor col={col} item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
};
